import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useUpdateCustomer } from "@/app/billing/api/hooks";
import type { UpdateCustomer } from "@/app/billing/types";
import { updateCustomerSchema } from "@/app/billing/types";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

type CustomerEditSheetProps = {
  currentName: string;
  trigger: React.ReactElement;
};

export const CustomerEditSheet = ({
  currentName,
  trigger,
}: CustomerEditSheetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { organizationId } = useOrgProjectRouteContext();

  const form = useForm<UpdateCustomer>({
    resolver: zodResolver(updateCustomerSchema),
    defaultValues: {
      name: currentName,
    },
  });

  const { mutate: updateCustomer, isPending } =
    useUpdateCustomer(organizationId);

  const handleSubmit = useCallback(
    (values: UpdateCustomer) => {
      try {
        updateCustomer(values, {
          onSuccess: () => {
            setIsOpen(false);
            form.reset({ name: values.name });
          },
        });
      } catch {
        toast.error("Unable to update customer information - please try again");
      }
    },
    [updateCustomer, form]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        // Reset form to current name when opening
        form.reset({ name: currentName });
      }
    },
    [form, currentName]
  );

  const handleError = useCallback(() => {
    toast.error("Please check your input and try again");
  }, []);

  return (
    <Sheet onOpenChange={handleOpenChange} open={isOpen}>
      <SheetTrigger render={trigger} />
      <SheetContent className="w-full max-w-2xl px-2">
        <ScrollArea className="h-full w-full">
          <SheetHeader>
            <SheetTitle className="mt-2">Edit Customer Information</SheetTitle>
            <SheetDescription>Update your customer details.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit(handleSubmit, handleError)}
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoFocus
                          placeholder="Enter your full name"
                        />
                      </FormControl>
                      <FormDescription>
                        This name will be displayed on your invoices and account
                        details.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-3">
                  <Button
                    className="flex-1"
                    disabled={isPending}
                    onClick={() => setIsOpen(false)}
                    type="button"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1" disabled={isPending} type="submit">
                    <p className="flex items-center justify-center gap-3">
                      {!!isPending && (
                        <Loader2 className="size-4 animate-spin" />
                      )}
                      Save Changes
                    </p>
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
